// src/components/booking/PractitionerRescheduleModal.js
// Modal for practitioner to propose a new time for a booking.
import React, { useState, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const REGION = 'us-west1';

const parseTime = (str) => { if (!str) return 0; const [h, m] = str.split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const formatTime = (minutes) => { const h = Math.floor(minutes / 60); const m = minutes % 60; const ampm = h >= 12 ? 'PM' : 'AM'; return `${h === 0 ? 12 : h > 12 ? h - 12 : h}:${m.toString().padStart(2, '0')} ${ampm}`; };
const formatDateDisplay = (date) => date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

const getTimezoneOffset = (tz, date) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(date);
    const offset = parts.find(p => p.type === 'timeZoneName')?.value || '';
    const match = offset.match(/GMT([+-]\d{1,2}(?::\d{2})?)/);
    if (match) { let v = match[1]; if (!v.includes(':')) v += ':00'; if (v.length === 5) v = v.replace(/^([+-])(\d):/, '$10$2:'); return v; }
    return '+00:00';
  } catch { return '+00:00'; }
};

const defaultSchedule = {
  monday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
  tuesday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
  wednesday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
  thursday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
  friday: { enabled: true, blocks: [{ start: '09:00', end: '17:00' }] },
  saturday: { enabled: false, blocks: [] },
  sunday: { enabled: false, blocks: [] },
};

const PractitionerRescheduleModal = ({ booking, clientName, onClose, onSuccess }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);

  // Load availability from localStorage
  const availability = JSON.parse(localStorage.getItem('practitionerAvailability') || '{}');
  const schedule = availability?.schedule && Object.keys(availability.schedule).length > 0
    ? availability.schedule : defaultSchedule;
  const bufferTime = Number(availability?.bufferTime) || 15;
  const bookingWindow = Number(availability?.bookingWindow) || 30;
  const timezone = availability?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const serviceDuration = Number(booking?.service?.duration) || 60;

  const generateTimeSlots = (date) => {
    if (!date) return [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const daySchedule = schedule[dayNames[date.getDay()]];
    if (!daySchedule?.enabled || !daySchedule?.blocks?.length) return [];
    const slots = [];
    const sixHoursFromNow = new Date(Date.now() + 6 * 3600000);
    daySchedule.blocks.forEach((block) => {
      let current = parseTime(block.start);
      const end = parseTime(block.end);
      while (current + serviceDuration <= end) {
        const slotDate = new Date(date);
        slotDate.setHours(Math.floor(current / 60), current % 60, 0, 0);
        if (slotDate >= sixHoursFromNow) slots.push({ time: current, label: formatTime(current) });
        current += serviceDuration + bufferTime;
      }
    });
    return slots;
  };

  const timeSlots = useMemo(() => generateTimeSlots(selectedDate), [selectedDate, serviceDuration]);

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();
  const isDateAvailable = (date) => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    if (!schedule[dayNames[date.getDay()]]?.enabled) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (date < today) return false;
    if (date > new Date(today.getTime() + bookingWindow * 86400000)) return false;
    return true;
  };
  const isSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const buildISO = () => {
    if (!selectedDate || selectedTime === null) return null;
    const hours = Math.floor(selectedTime / 60);
    const mins = selectedTime % 60;
    const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
    const tzDateStr = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(selectedDate);
    return `${tzDateStr}T${timeStr}${getTimezoneOffset(timezone, selectedDate)}`;
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const functions = getFunctions(undefined, REGION);
      const proposeReschedule = httpsCallable(functions, 'proposeReschedule');
      const result = await proposeReschedule({
        bookingId: booking.id,
        proposedDateTime: buildISO(),
        message: message.trim() || null,
      });
      setSent(true);
      setExpiresAt(result.data.expiresAt);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setSending(false);
    }
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthLabel = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // ── Success state ──
  if (sent) {
    const deadlineFormatted = expiresAt
      ? new Date(expiresAt).toLocaleString('en-US', { timeZone: timezone, weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
      : 'soon';

    return (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4 text-2xl">✓</div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Proposal sent!</h2>
            <p className="text-sm text-gray-600 mb-1">{clientName} has until:</p>
            <p className="text-sm font-semibold text-gray-900 mb-4">{deadlineFormatted}</p>
            <p className="text-xs text-gray-500 mb-6">If they don't respond, the original session stays as-is.</p>
            <button
              onClick={() => { onSuccess(); onClose(); }}
              className="w-full px-4 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
            >Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4" onClick={() => { if (!sending) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Propose a New Time</h2>
          <button onClick={onClose} disabled={sending} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current booking info */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 mb-4">
          <p className="text-sm font-semibold text-gray-900">{clientName}</p>
          <p className="text-sm text-gray-600">{booking?.service?.name} · {booking?._dateStr}</p>
        </div>

        {/* Calendar */}
        <div className="border border-gray-200 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-center mb-3">
            <button onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} className="p-1 border border-gray-200 rounded-md hover:bg-gray-50">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-gray-900">{monthLabel}</span>
            <button onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} className="p-1 border border-gray-200 rounded-md hover:bg-gray-50">
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
              <div key={d} className="text-xs font-semibold text-gray-400 py-1">{d}</div>
            ))}
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const date = new Date(year, month, day);
              const available = isDateAvailable(date);
              const selected = isSameDay(date, selectedDate);
              return (
                <button key={day} disabled={!available} onClick={() => { setSelectedDate(date); setSelectedTime(null); }}
                  className={`py-1.5 rounded-md text-xs font-medium transition-colors ${
                    selected ? 'bg-purple-600 text-white' : available ? 'text-gray-900 hover:bg-purple-50' : 'text-gray-300 cursor-default'
                  }`}
                >{day}</button>
              );
            })}
          </div>
        </div>

        {/* Time slots */}
        {selectedDate && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-600 mb-2">{formatDateDisplay(selectedDate)}</p>
            {timeSlots.length === 0 ? (
              <p className="text-xs text-gray-400">No available times (must be 6+ hours from now)</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {timeSlots.map(slot => (
                  <button key={slot.time} onClick={() => setSelectedTime(slot.time)}
                    className={`py-2 rounded-md text-xs font-medium transition-colors ${
                      selectedTime === slot.time
                        ? 'bg-purple-100 border-2 border-purple-600 text-purple-700'
                        : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >{slot.label}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Message to client */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Message to client (optional)</label>
          <div className="relative">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 300))}
              placeholder="Let your client know why you're rescheduling (optional)"
              disabled={sending}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <span className={`absolute bottom-2 right-3 text-xs ${message.length >= 280 ? 'text-red-500' : 'text-gray-400'}`}>
              {message.length}/300
            </span>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          <button onClick={onClose} disabled={sending}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >Cancel</button>
          <button onClick={handleSend} disabled={sending || !selectedDate || selectedTime === null}
            className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >{sending ? 'Sending...' : 'Send Proposal'}</button>
        </div>
      </div>
    </div>
  );
};

export default PractitionerRescheduleModal;
